<template>
  <div class="fb-unit">
    <a v-if="loadedUnit" :href="loadedUnit.url">
      <picture v-if="isLeaderTop" class="unit-image">
        <!-- webp -->
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.leaderXsImageWebp}`"
          media="(max-width: 516px)"
        />
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.leaderSmImageWebp}`"
          media="(min-width: 517px) and (max-width: 788px)"
        />
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.leaderMdImageWebp}`"
          media="(min-width: 789px) and (max-width: 1110px)"
        />
        <source type="image/webp" :srcset="`${loadedUnit.images.leaderLgImageWebp}`" />

        <!-- jpeg fallback -->
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.leaderXsImage}`"
          media="(max-width: 516px)"
        />
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.leaderSmImage}`"
          media="(min-width: 517px) and (max-width: 788px)"
        />
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.leaderMdImage}`"
          media="(min-width: 789px) and (max-width: 1110px)"
        />
        <source type="image/jpeg" :srcset="`${loadedUnit.images.leaderLgImage}`" />
        <img
          loading="lazy"
          :src="loadedUnit.images.leaderXsImage"
          alt=""
          @load="handleImpression"
        />
      </picture>

      <picture v-else-if="isLeaderInContent" class="unit-image">
        <!-- webp -->
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.rectangleMdImageWebp}`"
          media="(max-width: 516px)"
        />
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.leaderSmImageWebp}`"
          media="(min-width: 517px) and (max-width: 832px)"
        />
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.leaderSmImageWebp}`"
          media="(min-width: 941px) and (max-width: 1250px)"
        />
        <source type="image/webp" :srcset="`${loadedUnit.images.leaderMdImageWebp}`" />

        <!-- jpeg fallback -->
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.rectangleMdImage}`"
          media="(max-width: 516px)"
        />
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.leaderSmImage}`"
          media="(min-width: 517px) and (max-width: 832px)"
        />
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.leaderSmImage}`"
          media="(min-width: 941px) and (max-width: 1250px)"
        />
        <source type="image/jpeg" :srcset="`${loadedUnit.images.leaderMdImage}`" />
        <img
          loading="lazy"
          :src="loadedUnit.images.leaderXsImage"
          alt=""
          @load="handleImpression"
        />
      </picture>

      <picture v-else-if="isLeaderList" class="unit-image">
        <!-- webp -->
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.leaderXsImageWebp}`"
          media="(max-width: 516px)"
        />
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.leaderSmImageWebp}`"
          media="(min-width: 517px) and (max-width: 832px)"
        />
        <source
          type="image/webp"
          :srcset="`${loadedUnit.images.leaderSmImageWebp}`"
          media="(min-width: 941px) and (max-width: 1250px)"
        />
        <source type="image/webp" :srcset="`${loadedUnit.images.leaderMdImageWebp}`" />

        <!-- jpeg fallback -->
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.leaderXsImage}`"
          media="(max-width: 516px)"
        />
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.leaderSmImage}`"
          media="(min-width: 517px) and (max-width: 832px)"
        />
        <source
          type="image/jpeg"
          :srcset="`${loadedUnit.images.leaderSmImage}`"
          media="(min-width: 941px) and (max-width: 1250px)"
        />
        <source type="image/jpeg" :srcset="`${loadedUnit.images.leaderMdImage}`" />
        <img
          loading="lazy"
          :src="loadedUnit.images.leaderXsImage"
          alt=""
          @load="handleImpression"
        />
      </picture>

      <picture v-else class="unit-image">
        <!-- webp -->
        <source type="image/webp" :srcset="`${loadedUnit.images.rectangleMdImageWebp}`" />

        <!-- jpeg fallback -->
        <source type="image/jpeg" :srcset="`${loadedUnit.images.rectangleMdImage}`" />
        <img
          loading="lazy"
          :src="loadedUnit.images.rectangleMdImage"
          alt=""
          @load="handleImpression"
        />
      </picture>
    </a>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useUnitsStore } from '@/stores/units'
import { fbApi } from '@/utils/http-api'

/**
 * Internal units
 */
export default {
  name: 'FbUnit',
  props: {
    unitSlotId: {
      type: Number,
      required: true,
    },
    type: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      queuedIndex: null,
      impressionTracked: false,
    }
  },

  computed: {
    ...mapState(useUnitsStore, ['getUnit', 'isUnitPreview', 'removeLoadedUnitSlot']),

    loadedUnit() {
      return this.getUnit(this.unitSlotId, this.queuedIndex)
    },

    // any leaderboard
    isLeaderList() {
      return this.type === 'leader-list'
    },

    // leaderboard with largest 940w
    isLeaderTop() {
      return this.type === 'leader-top'
    },

    // leaderboard with largest 940w
    isLeaderInContent() {
      return this.type === 'leader-in-content'
    },
  },

  async created() {
    this.queuedIndex = await this.initUnitSlot(this.unitSlotId)
    // console.log('fb unit created', this.unitSlotId, this.queuedIndex)
  },

  async beforeUnmount() {
    // clean up to avoid blimps on pagination
    this.removeLoadedUnitSlot(this.unitSlotId, this.queuedIndex)
  },

  methods: {
    ...mapActions(useUnitsStore, ['initUnitSlot']),

    async handleImpression() {
      // no action if fb admin preview
      if (this.isUnitPreview) {
        return
      }

      // only track once
      if (this.impressionTracked) {
        return
      }

      // instantly flip, even if XHR fails
      this.impressionTracked = true

      try {
        await fbApi.put(`/units/${this.loadedUnit.id}/impression`, {
          // do not show error modal on 4xx
          skipErrorModal: true,
        })
      } catch (err) {
        // do not show error modal, just log
        console.error('impression error', err)
      }
    },
  },
}
</script>
